import axios from 'axios';
import config from '../config/config';

class Api {
  constructor() {
    // Validate configuration
    if (!config.defaultServerUrl) {
      console.error('[API] ERROR: config.defaultServerUrl is not set! Please check your configuration.');
      throw new Error('API configuration error: defaultServerUrl is missing');
    }
    
    this.baseUrl = localStorage.getItem('serverUrl') || config.defaultServerUrl;
    console.log('[API] Initialized with base URL:', this.baseUrl);
    
    this.axios = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests if available
    const token = localStorage.getItem('token');
    if (token) {
      this.setAuthToken(token);
    }

    // Add response interceptor for error handling
    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Handle 401 Unauthorized errors by trying to refresh the session once
        // before forcing a re-login. Login/refresh endpoints never retry.
        const { response, config } = error;
        const isAuthEndpoint =
          config?.url?.includes('/api/auth/login') ||
          config?.url?.includes('/api/auth/refresh');

        if (response && response.status === 401 && !isAuthEndpoint && !config?._retry) {
          const token = localStorage.getItem('token');
          if (token) {
            try {
              const newToken = await this.refreshToken();
              if (newToken) {
                config._retry = true;
                config.headers = config.headers || {};
                config.headers['Authorization'] = `Bearer ${newToken}`;
                return this.axios.request(config);
              }
            } catch (refreshError) {
              // Fall through to forced logout below.
            }
          }
        }

        if (response && response.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Exchange the current token for a fresh one via /api/auth/refresh
   * (sliding session). Returns the new token, or null on failure.
   * @returns {Promise<string|null>}
   */
  async refreshToken() {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const response = await axios.post(`${this.baseUrl}/api/auth/refresh`, { token });
      const newToken = response.data?.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
        this.setAuthToken(newToken);
        return newToken;
      }
    } catch (error) {
      console.error('[API] Token refresh failed:', error);
    }
    return null;
  }

  setBaseUrl(url) {
    this.baseUrl = url;
    this.axios.defaults.baseURL = url;
  }

  setAuthToken(token) {
    if (token) {
      this.axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.axios.defaults.headers.common['Authorization'];
    }
  }

  async get(url, config = {}) {
    return this.axios.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    console.log(`[API] POST ${url}`, { data, config });
    try {
      const response = await this.axios.post(url, data, config);
      console.log(`[API] POST ${url} - Success:`, response.data);
      return response;
    } catch (error) {
      console.error(`[API] POST ${url} - Error:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }

  async put(url, data = {}, config = {}) {
    return this.axios.put(url, data, config);
  }

  async delete(url, config = {}) {
    return this.axios.delete(url, config);
  }

  // Authentication methods
  async getSalt(email) {
    const response = await this.get(`/api/auth/salt?email=${encodeURIComponent(email)}`);
    return response.data;
  }

  async register(email, authHash, salt, deviceId = null) {
    const response = await this.post('/api/auth/register', {
      email,
      authHash,
      salt,
      deviceId
    });
    return response.data;
  }

  async login(email, authHash, deviceId = null) {
    const response = await this.post('/api/auth/login', {
      email,
      authHash,
      deviceId
    });
    return response.data;
  }

  async logout() {
    const response = await this.post('/api/auth/logout');
    return response.data;
  }

  async getMe() {
    const response = await this.get('/api/auth/me');
    return response.data;
  }

  // Settings methods
  async getSettings() {
    const response = await this.get('/api/settings');
    return response.data;
  }

  async updateSettings(settings) {
    const response = await this.post('/api/settings', settings);
    return response.data;
  }

  // FlashSync specific API methods

  async getSyncMetadata(type) {
    return this.get(`/api/sync/${type}/metadata`);
  }

  async uploadData(type, data) {
    return this.put(`/api/sync/${type}`, data);
  }

  async downloadData(type) {
    return this.get(`/api/sync/${type}`);
  }
}

const api = new Api();
export default api;
